export const normalizeMacAddress = (value) =>
  String(value || "").replace(/\\/g, "").trim().toUpperCase();

export const formatMacAddressInput = (value) => {
  const hex = String(value || "").replace(/[^0-9a-f]/gi, "").slice(0, 12).toUpperCase();
  return hex.match(/.{1,2}/g)?.join(":") || "";
};

export const isValidMacAddress = (value) =>
  /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(normalizeMacAddress(value));

const numberFrom = (row, keys) => {
  for (const key of keys) {
    const value = Number(row?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return 0;
};

const hasFiniteNumber = (row, keys) => keys.some((key) => {
  const value = row?.[key];
  if (value === "" || value === null || value === undefined) return false;
  return Number.isFinite(Number(value));
});

const stringFrom = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim()) return String(value).trim();
  }
  return "";
};

const expandCameraServiceRows = (source) =>
  source.flatMap((row) => {
    if (!Array.isArray(row?.records)) return [row];
    if (!row.records.length) return [];

    const envelopeMetadata = {
      ...(row?.people_counter_id != null ? { people_counter_id: row.people_counter_id } : {}),
      ...(row?.people_counter_name ? { people_counter_name: row.people_counter_name } : {}),
      ...(row?.organization_id != null ? { organization_id: row.organization_id } : {}),
      ...(row?.organization_name ? { organization_name: row.organization_name } : {}),
      ...(row?.mac_address ? { mac_address: row.mac_address } : {}),
      ...(row?.macAddress ? { macAddress: row.macAddress } : {}),
      ...(row?.device_name ? { device_name: row.device_name } : {}),
      ...(!row?.device_name && row?.name ? { device_name: row.name } : {}),
      ...(row?.ipAddress != null ? { ipAddress: row.ipAddress } : {}),
      ...(row?.siteId != null ? { siteId: row.siteId } : {}),
    };

    return row.records.map((record) => ({
      ...envelopeMetadata,
      ...(record && typeof record === "object" ? record : {}),
    }));
  });

export const normalizePeopleCounterRows = (payload) => {
  const rawSource = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];
  const source = expandCameraServiceRows(rawSource);

  return source.map((row, index) => {
    const macAddress = normalizeMacAddress(stringFrom(row, [
      "mac_address", "macAddress", "mac", "device_mac", "device_sn", "deviceSn",
    ]));
    const windowStart = stringFrom(row?.timeWindow || {}, ["start"])
      || stringFrom(row, ["window_start", "startTime", "start_time"]);
    const windowEnd = stringFrom(row?.timeWindow || {}, ["end"])
      || stringFrom(row, ["window_end", "endTime", "end_time"]);
    // createdAt is the actual persisted reading timestamp on the single-device endpoint.
    // Prefer it over the camera time window because some devices currently report the Unix epoch there.
    const receivedAt = stringFrom(row, [
      "received_at", "receivedAt", "timestamp", "created_at", "createdAt", "time",
    ]) || windowEnd || windowStart;
    const trafficEntered = numberFrom(row?.traffic || {}, ["entered"]) || numberFrom(row, ["entered"]);
    const trafficExited = numberFrom(row?.traffic || {}, ["exited"]) || numberFrom(row, ["exited"]);
    const details = row?.details && typeof row.details === "object" ? row.details : {};
    return {
      ...row,
      _row_id: row?._row_id || `${macAddress || "reading"}-${receivedAt || index}-${index}`,
      people_counter_name: stringFrom(row, ["people_counter_name", "device_name", "name"]) || "-",
      mac_address: macAddress || "-",
      organization_name: stringFrom(row, ["organization_name", "organizationName"]) || "-",
      line: stringFrom(row, ["line"]) || "-",
      line_name: stringFrom(row, ["line_name", "lineName"]) || "-",
      people_in: numberFrom(row, ["people_in", "peopleIn", "in"]) || trafficEntered,
      people_out: numberFrom(row, ["people_out", "peopleOut", "out"]) || trafficExited,
      traffic_entered: trafficEntered,
      traffic_exited: trafficExited,
      total_in: numberFrom(row, ["total_in", "totalIn"]) || trafficEntered,
      total_out: numberFrom(row, ["total_out", "totalOut"]) || trafficExited,
      total_inside: numberFrom(row, ["total_inside", "totalInside"]),
      _has_total_inside: hasFiniteNumber(row, ["total_inside", "totalInside"]),
      children_in: numberFrom(details, ["childrenIn"]) || numberFrom(row, ["childrenIn", "children_in"]),
      children_out: numberFrom(details, ["childrenOut"]) || numberFrom(row, ["childrenOut", "children_out"]),
      group_in: numberFrom(details, ["groupIn"]) || numberFrom(row, ["groupIn", "group_in"]),
      group_out: numberFrom(details, ["groupOut"]) || numberFrom(row, ["groupOut", "group_out"]),
      staff_in: numberFrom(details, ["staffIn"]) || numberFrom(row, ["staffIn", "staff_in"]),
      staff_out: numberFrom(details, ["staffOut"]) || numberFrom(row, ["staffOut", "staff_out"]),
      window_start: windowStart || null,
      window_end: windowEnd || null,
      received_at: receivedAt || null,
    };
  });
};

export const buildRealtimePeopleCounterRows = (payload, devices = [], receivedAt = new Date()) => {
  if (!payload || typeof payload !== "object") return [];

  const macAddress = normalizeMacAddress(
    payload.macAddress || payload.mac_address || payload.mac || payload.deviceMac
  );
  if (!macAddress) return [];

  const device = (Array.isArray(devices) ? devices : []).find(
    (item) => normalizeMacAddress(item?.mac_address || item?.macAddress) === macAddress
  );
  if (!device) return [];

  const sourceRecords = (Array.isArray(payload.newRecords)
    ? payload.newRecords
    : Array.isArray(payload.records)
      ? payload.records
      : []).slice(0, 200);
  if (!sourceRecords.length) return [];

  const fallbackTimestamp = receivedAt instanceof Date && !Number.isNaN(receivedAt.getTime())
    ? receivedAt.toISOString()
    : new Date().toISOString();

  return normalizePeopleCounterRows(sourceRecords.map((record, index) => {
    const row = record && typeof record === "object" ? record : {};
    const timestamp = stringFrom(row, [
      "received_at", "receivedAt", "timestamp", "created_at", "createdAt", "time",
    ]) || stringFrom(payload, ["received_at", "receivedAt", "timestamp", "created_at", "createdAt", "time"])
      || fallbackTimestamp;

    return {
      ...row,
      _row_id: row._row_id
        || `live-${macAddress}-${timestamp}-${stringFrom(row, ["line", "lineName", "line_name"]) || index}`,
      people_counter_id: device.people_counter_id,
      people_counter_name: device.name || device.people_counter_name || macAddress,
      organization_id: device.organization_id,
      organization_name: device.organization_name || device.organization?.name || "",
      mac_address: macAddress,
      macAddress,
      received_at: timestamp,
      _realtime: true,
    };
  }));
};

export const isPeopleCounterRowWithinTimeFilter = (row, timeFilter) => {
  if (!timeFilter?.fromCreatedAt || !timeFilter?.toCreatedAt) return true;
  const timestamp = new Date(row?.received_at || row?.createdAt || row?.created_at || 0).getTime();
  const from = new Date(timeFilter.fromCreatedAt).getTime();
  const to = new Date(timeFilter.toCreatedAt).getTime();
  if (![timestamp, from, to].every(Number.isFinite)) return false;
  return timestamp >= from && timestamp <= to;
};

const readingTime = (row) => {
  const value = new Date(row?.received_at || 0).getTime();
  return Number.isFinite(value) ? value : 0;
};

export const getLatestReadingByDevice = (rows) => {
  // A device can return several lines in one latest response. Keep the newest
  // reading for every device+line pair; collapsing only by MAC would silently
  // drop line2, line3, ... from totals and charts.
  const latest = new Map();
  rows.forEach((row, index) => {
    const deviceKey = normalizeMacAddress(row.mac_address) || String(row.people_counter_id || row._row_id);
    const lineKey = row?.line !== undefined && row?.line !== null && row?.line !== "-"
      ? String(row.line)
      : row?.line_name && row.line_name !== "-"
        ? String(row.line_name)
        : `row-${index}`;
    const key = `${deviceKey}::${lineKey}`;
    const current = latest.get(key);
    if (!current || readingTime(row) >= readingTime(current)) latest.set(key, row);
  });
  return [...latest.values()];
};

export const mergePeopleCounterRealtimeRows = (currentRows, realtimeRows, mode, timeFilter = null) => {
  const current = Array.isArray(currentRows) ? currentRows : [];
  const live = (Array.isArray(realtimeRows) ? realtimeRows : []).filter((row) =>
    mode !== "many" || isPeopleCounterRowWithinTimeFilter(row, timeFilter)
  );
  if (!live.length) return current;

  if (mode === "latest") {
    return getLatestReadingByDevice([...current, ...live]);
  }

  const seen = new Set();
  return [...live, ...current].filter((row) => {
    const key = String(row?._row_id || "");
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const buildPeopleCounterSummary = (rows, registeredDeviceCount = 0) => {
  const latestRows = getLatestReadingByDevice(rows);
  const reportingDevices = new Set(
    latestRows.map((row) => normalizeMacAddress(row.mac_address) || String(row.people_counter_id || ""))
      .filter(Boolean)
  ).size;

  // live_camera_update explicitly supplies totalInside. Treat it as the source
  // of truth for that camera's current occupancy. A camera may publish several
  // line records in one event, so use the newest explicit total once per camera
  // instead of summing the same camera-wide total for every line.
  const byDevice = new Map();
  latestRows.forEach((row) => {
    const key = normalizeMacAddress(row.mac_address) || String(row.people_counter_id || row._row_id);
    const current = byDevice.get(key) || { rows: [], explicit: null };
    current.rows.push(row);
    if (row?._has_total_inside) {
      if (!current.explicit || readingTime(row) >= readingTime(current.explicit)) current.explicit = row;
    }
    byDevice.set(key, current);
  });

  let currentOccupancy = 0;
  byDevice.forEach(({ rows: deviceRows, explicit }) => {
    if (explicit) {
      currentOccupancy += Math.max(0, Number(explicit.total_inside || 0));
      return;
    }
    const entered = deviceRows.reduce((sum, row) => sum + Number(row.total_in || row.people_in || 0), 0);
    const exited = deviceRows.reduce((sum, row) => sum + Number(row.total_out || row.people_out || 0), 0);
    currentOccupancy += Math.max(0, entered - exited);
  });

  return {
    registeredDevices: Number(registeredDeviceCount || 0),
    reportingDevices,
    peopleIn: rows.reduce((sum, row) => sum + Number(row.people_in || 0), 0),
    peopleOut: rows.reduce((sum, row) => sum + Number(row.people_out || 0), 0),
    currentOccupancy,
  };
};

export const buildPeopleCounterChartData = (rows) => {
  const latestRows = getLatestReadingByDevice(rows);
  const deviceKeys = new Set(
    latestRows.map((row) => normalizeMacAddress(row.mac_address) || String(row.people_counter_id || ""))
  );
  const showDeviceName = deviceKeys.size > 1;

  return latestRows.map((row) => {
    const lineLabel = row?.line_name && row.line_name !== "-"
      ? String(row.line_name)
      : row?.line !== undefined && row?.line !== null && row.line !== "-"
        ? `Line ${row.line}`
        : "Line";
    const deviceLabel = row.people_counter_name || row.mac_address || "Device";

    return {
      name: showDeviceName ? `${deviceLabel} — ${lineLabel}` : lineLabel,
      deviceName: deviceLabel,
      line: row.line,
      lineName: lineLabel,
      peopleIn: Number(row.total_in || row.people_in || 0),
      peopleOut: Number(row.total_out || row.people_out || 0),
    };
  }).slice(0, 40);
};
